import { useDisclose } from 'native-base'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { DiscountType, DiscountUnitType } from '../@types'
import { PromoCodeErrorCodes } from '../components/PromoCode/types'
import { PercentageDiscount } from '../hooks/use-cart-price'
import { useCartProduct } from '../hooks/use-cart-product'
import fetcher from '../lib/fetcher'

import { useAuth } from './auth'

type CampaignType = {
  id: number
  name: string
}

interface ReferralDiscount {
  value: number
  valueType: DiscountUnitType
}

export interface ReferralDiscountResponse {
  code: string
  campaign: CampaignType
  discount: ReferralDiscount
}

type DiscountsContextResult = {
  applySessionDiscount: (discount: Pick<PercentageDiscount, 'value' | 'code'>, type?: DiscountType | undefined) => void
  getSessionDiscount: () => PercentageDiscount | null
  removeSessionDiscount: () => void
  hasB2BDiscountForCartProduct: boolean
  setErrorPromoCode: Dispatch<SetStateAction<PromoCodeErrorCodes | null>>
  errorPromoCode: PromoCodeErrorCodes | null
  promoCodeDisclose: { isOpen: boolean; onOpen: () => void; onClose: () => void; onToggle: () => void }
}

export const DiscountsContext = React.createContext<DiscountsContextResult | undefined>(undefined)

export const DiscountsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [discountApplied, setDiscountApplied] = useState<PercentageDiscount | null>(null)
  const [errorPromoCode, setErrorPromoCode] = React.useState<PromoCodeErrorCodes | null>(null)
  const { product } = useCartProduct()
  const { profile } = useAuth()
  const promoCodeDisclose = useDisclose()
  const { t } = useTranslation()
  const { data: referralDiscountData } = useSWR<ReferralDiscountResponse[]>(
    profile?.id ? [`/patients/${profile.id}/discounts`, { auth: true }] : null,
    fetcher.api
  )

  const b2bDiscountForCartProduct = useMemo(() => {
    if (!product || !profile?.b2bCompany || !profile.b2bCompany.productDiscounts?.length) return false

    const b2bProductDiscount = profile?.b2bCompany.productDiscounts.find(
      productDiscount => productDiscount.product.id === product?.id
    )

    return b2bProductDiscount
  }, [product?.id, profile?.id])

  const hasB2BDiscountForCartProduct = !!b2bDiscountForCartProduct

  const applyB2BDiscount = () => {
    if (!b2bDiscountForCartProduct) return

    if (discountApplied?.type === DiscountType.ANONYMOUS_PROMO_CODE) {
      setErrorPromoCode(PromoCodeErrorCodes.INCOMPATIBLE_WITH_COMPANY_DISCOUNT)
    }

    return setDiscountApplied({
      code: '',
      name: t('screens.checkout.companyDiscount'),
      type: DiscountType.B2B_COMPANY_EMPLOYEE,
      unitType: DiscountUnitType.PERCENTAGE,
      value: b2bDiscountForCartProduct.discount,
    })
  }

  const applyHighestReferralDiscount = (referralDiscounts: ReferralDiscountResponse[]) => {
    const highestDiscount = referralDiscounts.sort((a, b) => b.discount.value - a.discount.value)[0]

    if (highestDiscount?.discount.valueType !== DiscountUnitType.PERCENTAGE) return

    const { campaign, discount, code } = highestDiscount
    return setDiscountApplied({
      code,
      name: campaign.name,
      type: DiscountType.REFERRAL,
      unitType: DiscountUnitType.PERCENTAGE,
      value: discount.value,
    })
  }

  useEffect(() => {
    if (hasB2BDiscountForCartProduct) {
      applyB2BDiscount()
    } else if (referralDiscountData) {
      applyHighestReferralDiscount(referralDiscountData)
    }
  }, [referralDiscountData, profile?.b2bCompany, product?.id, hasB2BDiscountForCartProduct])

  const applySessionDiscount = useCallback(
    (discount: Pick<PercentageDiscount, 'value' | 'code'>, type?: DiscountType) => {
      setDiscountApplied({
        ...discount,
        name: discount.code,
        type: type || DiscountType.PROMO_CODE,
        unitType: DiscountUnitType.PERCENTAGE,
      })
    },
    [setDiscountApplied]
  )
  const removeSessionDiscount = useCallback(() => setDiscountApplied(null), [setDiscountApplied])
  const getSessionDiscount = useCallback(() => discountApplied, [discountApplied])

  const context = useMemo<DiscountsContextResult>(
    () => ({
      errorPromoCode,
      promoCodeDisclose,
      applySessionDiscount,
      getSessionDiscount,
      removeSessionDiscount,
      hasB2BDiscountForCartProduct,
      setErrorPromoCode,
    }),
    [
      errorPromoCode,
      promoCodeDisclose,
      getSessionDiscount,
      applySessionDiscount,
      removeSessionDiscount,
      setErrorPromoCode,
    ]
  )

  return <DiscountsContext.Provider value={context}>{children}</DiscountsContext.Provider>
}

export const useDiscounts = () => {
  const context = useContext(DiscountsContext)

  if (!context) {
    throw new Error('useDiscounts() must be wrapped inside DiscountsProvider')
  }

  return context
}
